import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Baby, Plus, Trash, PencilSimple, MagnifyingGlass, Snowflake, Sun, Leaf, CloudSnow, Flower, WarningCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TEXTURES = [
  { value: 'pure', label: 'Puré' },
  { value: 'papa', label: 'Papa' },
  { value: 'pedacos_pequenos', label: 'Pedaços pequenos' },
  { value: 'finger_food', label: 'Finger food' },
];
const NUTRIENTS = [
  { value: 'ferro', label: 'Ferro' }, { value: 'vitamina_a', label: 'Vit. A' }, { value: 'vitamina_d', label: 'Vit. D' },
  { value: 'zinco', label: 'Zinco' }, { value: 'omega3', label: 'Ómega-3' }, { value: 'calcio', label: 'Cálcio' },
  { value: 'proteina', label: 'Proteína' }, { value: 'fibra', label: 'Fibra' },
];
const SEASONS = [
  { value: 'primavera', label: 'Primavera', icon: Flower }, { value: 'verao', label: 'Verão', icon: Sun },
  { value: 'outono', label: 'Outono', icon: Leaf }, { value: 'inverno', label: 'Inverno', icon: CloudSnow },
];

export default function BabyRecipeBankPage() {
  const confirmDelete = useConfirmDelete();
  const [recipes, setRecipes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [search, setSearch] = useState('');
  const [textureFilter, setTextureFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [freezerOnly, setFreezerOnly] = useState(false);
  const [nutrientFilter, setNutrientFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewRecipe, setViewRecipe] = useState(null);
  const [swaps, setSwaps] = useState(null);
  const [form, setForm] = useState({ name: '', instructions: '', textures: [], month_min: '', month_max: '', freezer_friendly: false, nutrient_focus: [], season_tags: [], photo_path: '', ingredients: [] });
  const [ingName, setIngName] = useState(''); const [ingQty, setIngQty] = useState(''); const [ingUnit, setIngUnit] = useState('g');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [usageWeek, setUsageWeek] = useState('');

  const fetchRecipes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (textureFilter) params.set('texture', textureFilter);
      if (monthFilter) params.set('month', monthFilter);
      if (freezerOnly) params.set('freezer_friendly', 'true');
      if (nutrientFilter) params.set('nutrient_focus', nutrientFilter);
      if (seasonFilter) params.set('season', seasonFilter);
      if (patientId) params.set('patient_id', patientId);
      const { data } = await axios.get(`${API}/api/baby-recipes?${params}`);
      setRecipes(data || []);
    } catch { toast.error('Erro ao carregar receitas'); } finally { setLoading(false); }
  }, [search, textureFilter, monthFilter, freezerOnly, nutrientFilter, seasonFilter, patientId]);

  const fetchPatients = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients?search=`); setPatients(data || []); } catch { setPatients([]); }
  }, []);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);
  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const resetForm = () => {
    setForm({ name: '', instructions: '', textures: [], month_min: '', month_max: '', freezer_friendly: false, nutrient_focus: [], season_tags: [], photo_path: '', ingredients: [] });
    setEditing(null);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name, instructions: r.instructions || '', textures: r.textures || [],
      month_min: r.month_min ?? '', month_max: r.month_max ?? '', freezer_friendly: !!r.freezer_friendly,
      nutrient_focus: r.nutrient_focus || [], season_tags: r.season_tags || [], photo_path: r.photo_path || '',
      ingredients: r.ingredients || [],
    });
    setShowForm(true);
  };

  const toggleArr = (field, value) => setForm(prev => ({ ...prev, [field]: prev[field].includes(value) ? prev[field].filter(v => v !== value) : [...prev[field], value] }));

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/api/upload`, fd);
      setForm(prev => ({ ...prev, photo_path: data.path || data.url || '' }));
    } catch { toast.error('Erro ao carregar foto'); } finally { setPhotoUploading(false); }
  };

  const addIngredient = () => {
    if (!ingName.trim()) return;
    setForm(prev => ({ ...prev, ingredients: [...prev.ingredients, { name: ingName.trim(), quantity: ingQty, unit: ingUnit }] }));
    setIngName(''); setIngQty('');
  };
  const removeIngredient = (idx) => setForm(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    const payload = {
      name: form.name, instructions: form.instructions, ingredients: form.ingredients,
      is_baby_recipe: true, textures: form.textures,
      month_min: form.month_min === '' ? null : parseInt(form.month_min),
      month_max: form.month_max === '' ? null : parseInt(form.month_max),
      freezer_friendly: form.freezer_friendly, nutrient_focus: form.nutrient_focus,
      season_tags: form.season_tags, photo_path: form.photo_path || null,
      is_public: true, category: '', categories: [], tags: [], servings: 1,
    };
    try {
      if (editing) await axios.put(`${API}/api/recipes/${editing.id}`, payload);
      else await axios.post(`${API}/api/recipes`, payload);
      toast.success(editing ? 'Receita atualizada' : 'Receita criada');
      setShowForm(false); resetForm(); fetchRecipes();
    } catch { toast.error('Erro ao guardar receita'); }
  };

  const handleDelete = async (id) => {
    const r = recipes.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a receita de bebé "${r?.name || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/recipes/${id}`); toast.success('Receita eliminada'); fetchRecipes(); } catch { toast.error('Erro ao eliminar'); }
  };

  const openSwaps = async (r) => {
    setViewRecipe(r); setSwaps(null);
    try { const { data } = await axios.get(`${API}/api/baby-recipes/${r.id}/seasonal-swaps`); setSwaps(data || []); } catch { setSwaps([]); }
  };

  const logUsage = async (recipeId) => {
    if (!patientId) { toast.error('Escolhe primeiro uma paciente (filtro no topo)'); return; }
    try {
      const { data } = await axios.post(`${API}/api/baby-recipes/${recipeId}/log-usage`, { patient_id: patientId, week_label: usageWeek });
      toast.success('Utilização registada');
      (data.warnings || []).forEach(w => toast.warning(w, { duration: 6000 }));
      fetchRecipes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="fade-in" data-testid="baby-recipe-bank-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-pink-100 flex items-center justify-center"><Baby size={22} className="text-pink-600" weight="duotone" /></div>
          <div>
            <h2 className="font-heading font-medium text-2xl text-brand-text">Receitas de Bebé</h2>
            <p className="text-sm text-brand-muted mt-0.5">Banco de receitas por textura, idade e sazonalidade - apoio à composição dos menus de bebé.</p>
          </div>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-baby-recipe-btn"><Plus size={16} /> Nova receita</button>
      </div>

      {/* Patient context for usage tracking */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="patient-context-panel">
        <Label className="text-xs text-brand-muted whitespace-nowrap">Ver avisos de repetição para:</Label>
        <Select value={patientId} onValueChange={setPatientId}>
          <SelectTrigger className="w-56" data-testid="patient-context-select"><SelectValue placeholder="Nenhuma (sem avisos)" /></SelectTrigger>
          <SelectContent>{patients.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        {patientId && (
          <Input value={usageWeek} onChange={e => setUsageWeek(e.target.value)} placeholder="Semana (ex: S3), opcional" className="w-48 rounded-xl border-brand-border text-sm" data-testid="usage-week-input" />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5 space-y-3" data-testid="baby-recipe-filters">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Procurar receita de bebé..." className="pl-9 rounded-xl border-brand-border" data-testid="baby-recipe-search" />
        </div>
        <div>
          <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1.5">Textura</p>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setTextureFilter('')} className={`px-2.5 py-1 rounded-full text-xs ${!textureFilter ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>Todas</button>
            {TEXTURES.map(t => (
              <button key={t.value} onClick={() => setTextureFilter(textureFilter === t.value ? '' : t.value)} className={`px-2.5 py-1 rounded-full text-xs ${textureFilter === t.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid={`filter-texture-${t.value}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-brand-muted whitespace-nowrap">Idade do bebé (meses)</Label>
            <Input type="number" min="4" max="36" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="w-20 rounded-xl border-brand-border" data-testid="month-filter-input" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-brand-text cursor-pointer">
            <input type="checkbox" checked={freezerOnly} onChange={e => setFreezerOnly(e.target.checked)} data-testid="freezer-filter-checkbox" /> <Snowflake size={13} /> Só congeláveis
          </label>
          <Select value={seasonFilter} onValueChange={v => setSeasonFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="season-filter-select"><SelectValue placeholder="Estação" /></SelectTrigger>
            <SelectContent><SelectItem value="_all">Todas as estações</SelectItem>{SEASONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1.5">Foco nutricional</p>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setNutrientFilter('')} className={`px-2.5 py-1 rounded-full text-xs ${!nutrientFilter ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>Todos</button>
            {NUTRIENTS.map(n => (
              <button key={n.value} onClick={() => setNutrientFilter(nutrientFilter === n.value ? '' : n.value)} className={`px-2.5 py-1 rounded-full text-xs ${nutrientFilter === n.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid={`filter-nutrient-${n.value}`}>{n.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center py-12 text-brand-muted">A carregar...</p>
      ) : recipes.length === 0 ? (
        <div className="text-center py-16"><Baby size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" /><p className="text-brand-muted">Sem receitas de bebé. Cria a primeira!</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {recipes.map(r => (
            <div key={r.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`baby-recipe-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => openSwaps(r)} className="text-left flex-1 min-w-0">
                  <p className="font-heading font-medium text-brand-text">{r.name}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {(r.textures || []).map(t => <span key={t} className="text-[9px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full">{TEXTURES.find(x => x.value === t)?.label || t}</span>)}
                    {(r.month_min || r.month_max) && <span className="text-[9px] bg-brand-bg text-brand-muted px-1.5 py-0.5 rounded-full">{r.month_min ?? '0'}-{r.month_max ?? '?'}m</span>}
                    {r.freezer_friendly && <span className="text-[9px] bg-sky-50 text-sky-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Snowflake size={9} /> Congela bem</span>}
                    {(r.nutrient_focus || []).map(n => <span key={n} className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">{NUTRIENTS.find(x => x.value === n)?.label || n}</span>)}
                  </div>
                  {r.recently_used_days_ago != null && (
                    <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-1.5" data-testid={`recent-use-warning-${r.id}`}>
                      <WarningCircle size={12} weight="duotone" /> Usada há {r.recently_used_days_ago} dia(s) para esta paciente
                    </p>
                  )}
                </button>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(r)} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`edit-baby-recipe-${r.id}`}><PencilSimple size={15} /></button>
                  <button onClick={() => handleDelete(r.id)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`delete-baby-recipe-${r.id}`}><Trash size={15} /></button>
                </div>
              </div>
              {patientId && (
                <button onClick={() => logUsage(r.id)} className="mt-2 text-[11px] text-brand-green font-medium hover:underline flex items-center gap-1" data-testid={`log-usage-btn-${r.id}`}>
                  <ArrowsClockwise size={12} /> Usei esta semana para a paciente selecionada
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* View + seasonal swaps */}
      <Dialog open={!!viewRecipe} onOpenChange={() => { setViewRecipe(null); setSwaps(null); }}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto">
          {viewRecipe && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-brand-text">{viewRecipe.name}</DialogTitle></DialogHeader>
              {viewRecipe.ingredients?.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs text-brand-muted uppercase tracking-wider mb-2">Ingredientes</h4>
                  <ul className="space-y-1">{viewRecipe.ingredients.map((ing, i) => <li key={i} className="text-sm text-brand-text">&bull; {ing.quantity} {ing.unit} {ing.name}</li>)}</ul>
                </div>
              )}
              {viewRecipe.instructions && (
                <div className="mb-4"><h4 className="text-xs text-brand-muted uppercase tracking-wider mb-2">Preparação</h4><p className="text-sm text-brand-text whitespace-pre-wrap">{viewRecipe.instructions}</p></div>
              )}
              <div>
                <h4 className="text-xs text-brand-muted uppercase tracking-wider mb-2">Sugestões de substituição sazonal</h4>
                {swaps === null ? <p className="text-xs text-brand-placeholder">A procurar...</p> : swaps.length === 0 ? (
                  <p className="text-xs text-brand-placeholder">Sem sugestões - cria mais receitas com textura semelhante para outras estações.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="seasonal-swaps-list">
                    {swaps.map(s => (
                      <div key={s.id} className="bg-brand-bg rounded-xl p-2.5 text-sm text-brand-text flex items-center justify-between">
                        <span>{s.name}</span>
                        <span className="flex gap-1">{(s.season_tags || []).map(se => <span key={se} className="text-[9px] bg-white px-1.5 py-0.5 rounded-full text-brand-muted">{SEASONS.find(x => x.value === se)?.label || se}</span>)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editing ? 'Editar receita de bebé' : 'Nova receita de bebé'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-sm text-brand-muted mb-1.5 block">Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-2xl border-brand-border" required data-testid="baby-recipe-name-input" /></div>

            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Fotografia</Label>
              <div className="flex items-center gap-3">
                {form.photo_path && <img src={`${API}/api/files/${encodeURIComponent(form.photo_path)}`} alt="" className="w-16 h-16 object-cover rounded-xl border border-brand-border" />}
                <label className="cursor-pointer border border-brand-border text-brand-muted rounded-xl px-3 py-1.5 text-xs hover:bg-brand-bg" data-testid="baby-recipe-photo-label">
                  {photoUploading ? 'A carregar...' : (form.photo_path ? 'Mudar foto' : 'Adicionar foto')}
                  <input type="file" accept="image/*" onChange={e => handlePhotoUpload(e.target.files?.[0])} disabled={photoUploading} className="hidden" data-testid="baby-recipe-photo-input" />
                </label>
              </div>
            </div>

            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Texturas <span className="text-[10px] text-brand-placeholder">(pode aplicar-se a várias fases)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {TEXTURES.map(t => <button key={t.value} type="button" onClick={() => toggleArr('textures', t.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.textures.includes(t.value) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`form-texture-${t.value}`}>{t.label}</button>)}
              </div>
            </div>

            <div className="flex gap-3">
              <div><Label className="text-sm text-brand-muted mb-1.5 block">Idade mín. (meses)</Label><Input type="number" min="4" max="36" value={form.month_min} onChange={e => setForm({ ...form, month_min: e.target.value })} className="w-28 rounded-2xl border-brand-border" data-testid="form-month-min" /></div>
              <div><Label className="text-sm text-brand-muted mb-1.5 block">Idade máx. (meses)</Label><Input type="number" min="4" max="36" value={form.month_max} onChange={e => setForm({ ...form, month_max: e.target.value })} className="w-28 rounded-2xl border-brand-border" data-testid="form-month-max" /></div>
            </div>

            <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
              <input type="checkbox" checked={form.freezer_friendly} onChange={e => setForm({ ...form, freezer_friendly: e.target.checked })} data-testid="form-freezer-checkbox" /> <Snowflake size={14} /> Congela bem
            </label>

            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Foco nutricional</Label>
              <div className="flex flex-wrap gap-1.5">
                {NUTRIENTS.map(n => <button key={n.value} type="button" onClick={() => toggleArr('nutrient_focus', n.value)} className={`px-2.5 py-1 rounded-full text-xs ${form.nutrient_focus.includes(n.value) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`form-nutrient-${n.value}`}>{n.label}</button>)}
              </div>
            </div>

            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Estação recomendada <span className="text-[10px] text-brand-placeholder">(vazio = todo o ano)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {SEASONS.map(s => <button key={s.value} type="button" onClick={() => toggleArr('season_tags', s.value)} className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${form.season_tags.includes(s.value) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`form-season-${s.value}`}><s.icon size={12} /> {s.label}</button>)}
              </div>
            </div>

            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Ingredientes</Label>
              {form.ingredients.length > 0 && (
                <ul className="space-y-1 mb-2">
                  {form.ingredients.map((ing, idx) => (
                    <li key={idx} className="flex items-center justify-between text-sm bg-brand-bg/50 rounded-xl px-3 py-1.5">
                      <span>{ing.quantity} {ing.unit} {ing.name}</span>
                      <button type="button" onClick={() => removeIngredient(idx)} className="text-red-400 hover:text-red-600"><Trash size={13} /></button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <Input value={ingName} onChange={e => setIngName(e.target.value)} placeholder="Ingrediente" className="flex-1 rounded-xl border-brand-border text-sm" data-testid="baby-ing-name-input" />
                <Input value={ingQty} onChange={e => setIngQty(e.target.value)} placeholder="Qtd" type="number" className="w-20 rounded-xl border-brand-border text-sm" data-testid="baby-ing-qty-input" />
                <Input value={ingUnit} onChange={e => setIngUnit(e.target.value)} placeholder="Un." className="w-16 rounded-xl border-brand-border text-sm" data-testid="baby-ing-unit-input" />
                <button type="button" onClick={addIngredient} className="text-brand-green text-sm px-2" data-testid="add-baby-ing-btn"><Plus size={16} /></button>
              </div>
            </div>

            <div><Label className="text-sm text-brand-muted mb-1.5 block">Modo de preparação</Label><Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} className="rounded-2xl border-brand-border resize-none" rows={4} data-testid="baby-recipe-instructions-input" /></div>

            <button type="submit" className="w-full bg-brand-green text-white rounded-full px-6 py-3.5 font-medium" data-testid="save-baby-recipe-btn">{editing ? 'Guardar alterações' : 'Criar receita'}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
